"use client";

import type { ReactNode } from "react";

import { cn } from "@/lib/utils/cn";
import { Drawer } from "./drawer";
import { uiRadius } from "./tokens";

export type AppLayoutProps = {
  sidebar: ReactNode;
  mobileNav: ReactNode;
  header: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
  className?: string;
  hideSidebar?: boolean;
  sidebarExpanded?: boolean;
  sidebarPinned?: boolean;
  mobileNavOpen?: boolean;
  onMobileNavClose?: () => void;
  onSidebarPointerEnter?: () => void;
  onSidebarPointerLeave?: () => void;
};

export function AppLayout({
  sidebar,
  mobileNav,
  header,
  toolbar,
  children,
  className,
  hideSidebar = false,
  sidebarExpanded = true,
  sidebarPinned = true,
  mobileNavOpen = false,
  onMobileNavClose = () => undefined,
  onSidebarPointerEnter,
  onSidebarPointerLeave,
}: AppLayoutProps) {
  const sidebarOffset = hideSidebar
    ? "md:ml-0"
    : sidebarExpanded
      ? "md:ml-[var(--sidebar-width)]"
      : "md:ml-[var(--sidebar-width-collapsed)]";

  return (
    <div className={cn("min-h-screen bg-surface-canvas text-foreground", className)}>
      {!hideSidebar ? (
        <aside
          className={cn(
            "workspace-sidebar fixed inset-y-0 left-0 z-40 hidden border-r border-card-border bg-[var(--sidebar-bg)] transition-[width,box-shadow] duration-slow ease-out md:flex",
            sidebarExpanded ? "w-[var(--sidebar-width)]" : "w-[var(--sidebar-width-collapsed)]",
            sidebarExpanded && !sidebarPinned ? "shadow-overlay" : "shadow-raised",
          )}
          aria-label="Primary navigation"
          aria-expanded={sidebarExpanded}
          onPointerEnter={onSidebarPointerEnter}
          onPointerLeave={onSidebarPointerLeave}
        >
          <div className="scrollbar-hidden flex min-h-0 w-full flex-col overflow-x-hidden overflow-y-auto">{sidebar}</div>
        </aside>
      ) : null}

      <div className={cn("min-w-0 transition-[margin] duration-slow ease-out", sidebarOffset)}>
        <div className="sticky top-0 z-30 border-b border-card-border bg-[var(--app-header-surface)] shadow-raised backdrop-blur-md">
          {header}
        </div>
        {toolbar ? <div className="border-b border-card-border bg-surface-secondary">{toolbar}</div> : null}
        <main id="main-content" className="relative flex min-h-[calc(100vh-var(--topbar-height))] flex-col overflow-x-hidden [scrollbar-gutter:stable]">
          {children}
        </main>
      </div>

      {!hideSidebar ? (
        <Drawer open={mobileNavOpen} onClose={onMobileNavClose} title="Navigation" description="Workspace sections" side="left" className="workspace-mobile-sidebar w-[min(88vw,22rem)]">
          <nav aria-label="Mobile navigation">{mobileNav}</nav>
        </Drawer>
      ) : null}
    </div>
  );
}

export const appChromeShellClass = uiRadius.shell;
